import type { ComponentType } from 'react'
import { ADMINISTRACAO, ANALISE_POR_IA, CONTA } from './navegacaoAdministracao'
import { APURACAO, CONTRATO } from './navegacaoContrato'
import { DOCUMENTOS_DO_CONTRATO } from './navegacaoDocumentos'
import { MUDANCAS, VISAO_GERAL } from './navegacaoMudancas'
import type { PapelContratual, UsuarioLogado } from '../types/gmud'

/**
 * A arquitetura de informação do sistema, declarada num lugar só.
 *
 * O menu e as rotas de "em construção" saem daqui, e é isso que o registro existe para garantir:
 * acrescentar uma tela futura é acrescentar um item, e o item aparece no menu E ganha rota no
 * mesmo instante. No sistema anterior o menu era markup na Blade e as rotas eram outra lista no
 * `web.php` — as duas divergiram, e sobrou item de menu apontando para rota que não existia mais.
 *
 * Todo o menu é declarado desde já, inclusive o que ainda não foi construído, porque quem usa o
 * sistema precisa saber o que vem e o que não vem: menu que cresce em silêncio a cada entrega faz
 * o fiscal descobrir uma tela nova por acidente, meses depois de ela existir.
 */

/** Se a tela existe ou se ainda é uma promessa declarada. */
export type SituacaoDaTela = 'pronto' | 'em-construcao'

export interface ItemDeNavegacao {
  /** Identificador estável. Não é rótulo: renomear o item na interface não pode trocar a chave. */
  id: string
  /** O que o usuário lê. Sai do glossário sempre que o termo já existe lá (Regra nº 4). */
  rotulo: string
  /**
   * O caminho da rota, com o recorte na query string quando ele faz parte do item — o histórico
   * do sistema anterior é `/gmuds?origem=MIGRADO_V1`, a mesma lista com um filtro.
   */
  para: string
  icone: ComponentType<{ className?: string }>
  /**
   * Permissão de `GET /me` que faz o item aparecer. Ausente = todo mundo vê.
   *
   * Isto é conveniência de menu, NÃO autorização: quem digitar a rota na barra de endereço chega
   * na tela e recebe a recusa do servidor, que é onde a regra mora de verdade.
   */
  permissao?: string
  /**
   * De que lado do contrato é preciso estar para a tela fazer sentido.
   *
   * Ausente = os dois lados. Presente = só aquele lado, e é o caso da fiscalização: quem é medido
   * não vê a medição. A recusa de verdade continua sendo do servidor (`@PreAuthorize` com
   * `ORG_CONTRATANTE`); aqui o item some do menu e a rota responde "sem acesso" em vez de uma tela
   * que carrega vazia com um 403 no console.
   */
  organizacao?: PapelContratual
  situacao: SituacaoDaTela
  /** Uma linha, em linguagem de quem não é de TI, dizendo para que serve a tela. */
  resumo: string
  /** O que a tela vai fazer quando existir. Só em item `em-construcao`. */
  oQueVaiTer?: string[]
  /** Fase do plano em que a tela entra, quando o plano a prevê. Ausente = ainda sem data. */
  fase?: string
  /** Endereço da função equivalente no sistema anterior, quando ela existe e funciona lá. */
  noSistemaAnterior?: string
}

export interface GrupoDeNavegacao {
  id: string
  rotulo: string
  itens: ItemDeNavegacao[]
}

/**
 * O menu inteiro, na ordem em que aparece.
 *
 * Os itens de cada grupo estão em `navegacaoMudancas.ts`, `navegacaoContrato.ts` e
 * `navegacaoAdministracao.ts`, partidos por causa do teto de 200 linhas. A ordem do menu é decisão
 * de arquitetura de informação e fica aqui, à vista, e não espalhada pelo arquivo em que cada grupo
 * por acaso mora. Os dois últimos são especiais na barra lateral: "Análise por IA" é o bloco com
 * dinheiro, e "conta" vai para o rodapé sem cabeçalho.
 */
export const NAVEGACAO: GrupoDeNavegacao[] = [
  VISAO_GERAL,
  MUDANCAS,
  CONTRATO,
  APURACAO,
  DOCUMENTOS_DO_CONTRATO,
  ADMINISTRACAO,
  ANALISE_POR_IA,
  CONTA,
]

/** Todos os itens, já sem os grupos. */
export function itensDeNavegacao(): ItemDeNavegacao[] {
  return NAVEGACAO.flatMap((grupo) => grupo.itens)
}

/**
 * O caminho de `para`, sem o recorte.
 *
 * Rota e recorte são coisas diferentes: `/gmuds?origem=MIGRADO_V1` é a rota `/gmuds`. Registrar a
 * string inteira como caminho criaria uma rota que nunca casa e um item de menu morto.
 *
 * @param item item do registro
 * @returns só a parte de caminho
 */
export function caminhoDe(item: ItemDeNavegacao): string {
  return item.para.split('?')[0] ?? item.para
}

/** Os itens que ainda não têm tela. O roteador monta uma rota para cada um. */
export function itensEmConstrucao(): ItemDeNavegacao[] {
  return itensDeNavegacao().filter((item) => item.situacao === 'em-construcao')
}

/**
 * Este item é para este usuário?
 *
 * Duas perguntas, na ordem em que custam: o lado do contrato (que não muda nunca) e a permissão
 * grossa declarada pelo servidor. Usuário sem lado cadastrado não vê tela restrita — é o mesmo
 * default-deny do backend, onde a ausência de `ORG_CONTRATANTE` recusa em vez de liberar.
 *
 * @param item item do registro
 * @param usuario usuário logado, ou nulo enquanto o `GET /me` não respondeu
 * @returns verdadeiro quando o item deve aparecer
 */
export function itemVisivelPara(item: ItemDeNavegacao, usuario: UsuarioLogado | null): boolean {
  if (item.organizacao && usuario?.papelContratual !== item.organizacao) {
    return false
  }
  return !item.permissao || (usuario?.permissoes.includes(item.permissao) ?? false)
}

/**
 * As rotas que exigem um lado do contrato, para o roteador saber quais proteger.
 *
 * @param organizacao o lado exigido
 * @returns os caminhos (sem recorte) restritos àquele lado
 */
export function caminhosRestritosA(organizacao: PapelContratual): string[] {
  return itensDeNavegacao()
    .filter((item) => item.organizacao === organizacao)
    .map(caminhoDe)
}

/**
 * Qual item do menu corresponde ao endereço atual.
 *
 * Não dá para usar só `NavLink end`: `/gmuds` e `/gmuds/novo` são dois itens, `/gmuds/7` não é
 * nenhum dos dois e ainda assim está dentro de "Solicitações", e o histórico é a mesma rota da
 * lista com um recorte. Por isso a escolha é por peso: caminho mais específico ganha, e entre
 * caminhos iguais ganha quem também casa o recorte pedido.
 *
 * @param caminho `location.pathname`
 * @param busca `location.search`
 * @returns o item correspondente, ou nulo fora do menu
 */
export function itemAtivo(caminho: string, busca: string): ItemDeNavegacao | null {
  const consulta = new URLSearchParams(busca)
  let escolhido: ItemDeNavegacao | null = null
  let maior = -1

  for (const item of itensDeNavegacao()) {
    const peso = pesoDoItem(item, caminho, consulta)
    if (peso > maior) {
      maior = peso
      escolhido = item
    }
  }

  return maior < 0 ? null : escolhido
}

/**
 * O grupo do menu a que o item pertence, para a trilha da barra superior ("Mudanças › Quadro").
 *
 * @param item item do registro
 * @returns o grupo, ou nulo se o item não estiver no menu
 */
export function grupoDoItem(item: ItemDeNavegacao): GrupoDeNavegacao | null {
  return NAVEGACAO.find((grupo) => grupo.itens.some((outro) => outro.id === item.id)) ?? null
}

/** Quanto este item casa com o endereço. Negativo = não casa. */
function pesoDoItem(item: ItemDeNavegacao, caminho: string, consulta: URLSearchParams): number {
  const rota = caminhoDe(item)
  if (caminho !== rota && !caminho.startsWith(`${rota}/`)) {
    return -1
  }

  const recorte = new URLSearchParams(item.para.slice(rota.length))
  for (const [chave, valor] of recorte) {
    // O recorte tem de estar de fato aplicado. Sem esta checagem, "Histórico do sistema anterior"
    // ficaria aceso na lista sem filtro nenhum — e a marcação do histórico é justamente o que não
    // pode ficar ambígua.
    if (!consulta.getAll(chave).includes(valor)) {
      return -1
    }
  }

  return rota.length * 2 + (item.para.includes('?') ? 1 : 0)
}
